import { createContext, useEffect, useReducer } from "react";
import {
  LOGOUT_EVENT_KEY,
  MAX_TIMEOUT_MS,
  getInitialAuthToken,
  getTokenExpiryMs,
  isTokenValid,
  logout,
  resetLogout,
  setLogoutHandler,
} from "../utils/authSession";

export const AuthContext = createContext();
export const authReducer = (state, action) => {
  switch (action.type) {
    case "LOGIN":
      return { user: action.payload };
    case "LOGOUT":
      return { user: null };
    default:
      return state;
  }
};

// Only a token whose JWT exp is still in the future is ever restored (cookie, or
// localStorage when "keep me logged in" was used); expired/malformed ones are cleared.
const value = getInitialAuthToken();
export const AuthContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    user: value,
  });

  // the central logout (utils/authSession) clears the context user through this
  useEffect(() => {
    setLogoutHandler(() => dispatch({ type: "LOGOUT" }));
    return () => setLogoutHandler(null);
  }, []);

  // schedule logout from the token's own exp; re-run on login, cleared on logout/unmount
  useEffect(() => {
    if (!state.user) return undefined;
    resetLogout();
    const expiryMs = getTokenExpiryMs(state.user);
    if (expiryMs === null) {
      logout();
      return undefined;
    }
    let timer = null;
    // setTimeout cannot exceed ~2^31 ms, so wake up early and re-check when needed
    const schedule = () => {
      clearTimeout(timer);
      const remaining = expiryMs - Date.now();
      if (remaining <= 0) {
        logout();
        return;
      }
      timer = setTimeout(schedule, Math.min(remaining, MAX_TIMEOUT_MS));
    };
    // timers can be throttled or paused (sleep, background tab): re-check on return
    const checkExpiry = () => {
      if (document.visibilityState !== "hidden" && !isTokenValid(state.user)) {
        logout();
      }
    };
    schedule();
    document.addEventListener("visibilitychange", checkExpiry);
    window.addEventListener("focus", checkExpiry);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", checkExpiry);
      window.removeEventListener("focus", checkExpiry);
    };
  }, [state.user]);

  // another tab logged out (logout marker, or authToken removed from localStorage)
  useEffect(() => {
    const onStorage = (event) => {
      if (
        event.key === LOGOUT_EVENT_KEY ||
        (event.key === "authToken" && event.newValue === null)
      ) {
        logout({ broadcast: false });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, dispatch }}>
      {children}
    </AuthContext.Provider>
  );
};
